/* eslint-disable react/jsx-closing-tag-location */
import _ from 'underscore';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ArrowSync16Filled } from '@fluentui/react-icons';

import {
  TilesGridCellImage as CellImage,
  TilesGridCellDecorativeLabel as CellLabel,
  TilesGridCellCardWithOverlay,
  TilesGridCellSelectionDecorator,
  TilesGridCellDeletionButtonDecorator,
  TilesGridCellDragDecorator,
} from '../../ads-studio-tile-grid';
import { TextWithHighlight } from '../../text-with-highlight';
import { CSMLAssetSource, ManualAddedImageSource } from '../../../model';
import { InfoSparkleSVGIcon } from '../../../asset-images';
import { useImageAssetsGridCellStyle } from './style';

export const AdsStudioImageAssetsGridCell = ({
  item,
  highlightedText,
  isOverlayEnabled,
  isCellFocused,
  renderCellContent,
  renderOverlayContent,
  i18n,
  selectable,
  selected,
  isMultiSelecting, // is in multi-selection mode, need to show checkbox on cells
  onClick,
  deletable,
  onDelete,
  draggable,
  onDrag,
  selectedItemCount,
}) => {
  const [isImageLoaded, setImageLoaded] = useState(false);
  const classes = useImageAssetsGridCellStyle();

  const {
    name,
    url,
    id,
    updating,
    sourceType,
    manualAddedImageSourceType,
  } = item || {};

  const cellContent = useMemo(() => {
    const defaultContent = (
      <Fragment>
        <CellImage
          src={url}
          alt={name || id || ''}
          showSpinner={false}
          onLoaded={setImageLoaded}
          i18n={i18n}
        />
        <div className={classes.imageCellLabelContainer}>
          {manualAddedImageSourceType === ManualAddedImageSource.Library && (
            <CellLabel
              text={i18n.getString(_TL_('Library'))}
              customClass={{ iconTextWrapper: 'image-source-label' }}
            />
          )}
          {manualAddedImageSourceType === ManualAddedImageSource.ShutterStock && (
            <CellLabel
              text={i18n.getString(_TL_('Stock'))}
              customClass={{ iconTextWrapper: 'image-source-label' }}
            />
          )}
          {(sourceType === CSMLAssetSource.AIGenerated || sourceType === CSMLAssetSource.BackgroundChangeGenerated || sourceType === CSMLAssetSource.AIGeneratedByRecraft) && (
            <CellLabel
              icon={InfoSparkleSVGIcon}
              customClass={{ iconTextWrapper: 'created-with-ai-label' }}
            />
          )}
          {updating && (
            <CellLabel
              text={i18n.getString(_TL_('Updating'))}
              icon={ArrowSync16Filled}
              customClass={{ iconTextWrapper: 'updating-label-icon-and-text' }}
            />
          )}
        </div>
      </Fragment>
    );
    if (renderCellContent) {
      return renderCellContent(defaultContent);
    }
    return defaultContent;
  }, [classes.imageCellLabelContainer, i18n, id, manualAddedImageSourceType, name, renderCellContent, sourceType, updating, url]);

  const funcRenderOverlayContent = useCallback(() => {
    if (renderOverlayContent) {
      return renderOverlayContent();
    }
    return null;
  }, [renderOverlayContent]);

  const onCellClick = useCallback(() => {
    if (selectable) {
      onClick({ item, selected });
    }
  }, [item, onClick, selectable, selected]);

  const withSelectionDecorator = useCallback(decoratedContent => (
    selectable ? (
      <TilesGridCellSelectionDecorator item={item} selected={selected} onClick={onClick} ariaLabel={name || i18n.getString(_TL_('Image'))} isFocused={isCellFocused} >
        {decoratedContent}
      </TilesGridCellSelectionDecorator>
    ) : decoratedContent
  ), [i18n, isCellFocused, item, name, onClick, selectable, selected]);

  const withDeleteButtonDecorator = useCallback(decoratedContent => (
    deletable ? (
      <TilesGridCellDeletionButtonDecorator item={item} onDelete={onDelete}>
        {decoratedContent}
      </TilesGridCellDeletionButtonDecorator>
    ) : decoratedContent
  ), [deletable, item, onDelete]);

  return (
    <div className={classnames('tiles-grid-cell-content', classes.imageAssetsGridCellContainer)}>
      <TilesGridCellDragDecorator
        draggable={draggable}
        item={item}
        dragItemsLength={selectedItemCount}
        onDrag={onDrag}
        i18n={i18n}
        selected={selected}
      >
        <TilesGridCellCardWithOverlay
          isCellFocused={isCellFocused}
          isOverlayEnabled={isOverlayEnabled && isImageLoaded}
          renderOverlayContent={funcRenderOverlayContent}
          customCellCardClass={(isOverlayEnabled && !selected && !isMultiSelecting) ? classes.selectableCardContainerAutoHideCheckbox : classes.selectableCardContainer}
          onClick={onCellClick}
        >
          {_.compose(withDeleteButtonDecorator, withSelectionDecorator)(cellContent)}
        </TilesGridCellCardWithOverlay>
      </TilesGridCellDragDecorator>
      <div className="cell-description">
        <div className="cell-description-name">
          <TextWithHighlight text={name || ''} highlightedText={highlightedText} enableTooltip />
        </div>
        {id && (
          <div className="cell-description-id">
            {`${i18n.getString(_TL_('ID'))} `}
            <TextWithHighlight text={id} highlightedText={highlightedText} />
          </div>
        )}
      </div>
    </div>
  );
};

AdsStudioImageAssetsGridCell.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
    createdTime: PropTypes.instanceOf(Date),
    // cropping task progress
    updating: PropTypes.bool,
  }).isRequired,
  highlightedText: PropTypes.string,
  isOverlayEnabled: PropTypes.bool,
  isCellFocused: PropTypes.bool,
  renderCellContent: PropTypes.func,
  renderOverlayContent: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onClick: () => null,
  isMultiSelecting: PropTypes.bool,
  deletable: PropTypes.bool,
  onDelete: PropTypes.func,
  draggable: PropTypes.bool,
  onDrag: PropTypes.func,
  selectedItemCount: PropTypes.number,
};

AdsStudioImageAssetsGridCell.defaultProps = {
  highlightedText: undefined,
  isOverlayEnabled: false,
  isCellFocused: false,
  renderCellContent: null,
  renderOverlayContent: null,
  selectable: false,
  selected: false,
  onClick: undefined,
  isMultiSelecting: false,
  deletable: false,
  onDelete: undefined,
  draggable: false,
  onDrag: _.noop,
  selectedItemCount: 1,
};
